import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useProfileChildren } from './profile';
import { getKidFromChild } from '../../utils/children';

export type CheckoutKid = {
  id: number;
  firstName: string;
  birthdate: string;
  isChosen: boolean;
  isEligible: boolean;
};
const CheckoutKidsStateContext = React.createContext<CheckoutKid[]>([]);
const SetChosenCheckoutKidsStateContext = React.createContext<React.Dispatch<
  React.SetStateAction<number[]>
> | null>(null);

export const CheckoutProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const kids = useProfileChildren();

  const [chosenKidsIds, setChosenKidsIds] = useState(kids.map((k) => k.id));

  const checkoutKids = useMemo(
    () =>
      kids
        .map((c) => getKidFromChild(c))
        .map((k) => ({
          ...k,
          isChosen: chosenKidsIds.includes(k.id),
        })),
    [kids, chosenKidsIds]
  );
  return (
    <SetChosenCheckoutKidsStateContext.Provider value={setChosenKidsIds}>
      <CheckoutKidsStateContext.Provider value={checkoutKids}>
        {children}
      </CheckoutKidsStateContext.Provider>
    </SetChosenCheckoutKidsStateContext.Provider>
  );
};

export const useCheckoutKidsInfo = () => useContext(CheckoutKidsStateContext);
export const useSetChosenCheckoutKids = () => {
  const setChosenIds = useContext(SetChosenCheckoutKidsStateContext);
  if (!setChosenIds) {
    throw new Error(
      'useSetChosenCheckoutKids used outside of CheckoutProvider'
    );
  }
  const select = useCallback(
    (kidId: number) =>
      setChosenIds((kidIds) => Array.from(new Set([...kidIds, kidId]))),
    []
  );
  const deselect = useCallback(
    (kidId: number) =>
      setChosenIds((kidIds) => kidIds.filter((id) => id !== kidId)),
    []
  );
  return { select, deselect };
};
